"use client";

import React, { memo, useState, useEffect, useCallback, useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/autoplay";
import "swiper/css/pagination";

import CustomButton from "@/components/common/button";
import WatchPlayIcon from "@/helpers/svg/watchPlayIcon.svg";
import { getShowcaseImageURL, parseShowcaseTargetURI } from "@/components/showcase/showcase.utils";
import Link from "next/link";

// CDN image params
const LOW_RES_PARAMS = { quality: 10, width: 40 };
const HI_RES_BG = { quality: 90, width: 1680 };
const HI_RES_OVERLAY = { quality: 90, width: 400 };
const HI_RES_STATIC = { quality: 90, width: 800 };

// Tiny base64 placeholder for initial paint before low-res loads
const BLUR_PLACEHOLDER = "data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGxwf/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltJagyeH0AthI5xdrLcNM91BF5pX2HaH9bcfaSXWGaRmknyJckliyjqTzSlT54b6bk+h0R7";

const BannerSlider = ({ items }) => {
  // Track which slides have been upgraded to high-res
  const [loadedSlides, setLoadedSlides] = useState(new Set());
  const timerRef = useRef(null);

  // Mark a slide as loaded (high-res)
  const markLoaded = useCallback((index) => {
    setLoadedSlides((prev) => {
      if (prev.has(index)) return prev;
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  }, []);

  // Progressive loading: active slide immediately, then 1s per remaining slide
  useEffect(() => {
    if (!items?.length) return;

    // Load first slide immediately
    markLoaded(0);

    // Queue remaining slides one by one, 1s apart
    const remaining = items.slice(1).map((_, i) => i + 1);
    let step = 0;

    const loadNext = () => {
      if (step < remaining.length) {
        markLoaded(remaining[step]);
        step++;
        timerRef.current = setTimeout(loadNext, 1000);
      }
    };

    // Start the chain after 1s
    timerRef.current = setTimeout(loadNext, 1000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [items, markLoaded]);

  // When swiper slide changes, immediately load that slide if not already loaded
  const handleSlideChange = useCallback((swiper) => {
    const realIndex = swiper.realIndex ?? swiper.activeIndex;
    markLoaded(realIndex);
  }, [markLoaded]);

  // Get image URL: low-res or high-res based on whether slide is loaded
  const getImageSrc = useCallback((imageName, hiResParams, slideIndex) => {
    if (loadedSlides.has(slideIndex)) {
      return getShowcaseImageURL(imageName, hiResParams);
    }
    return getShowcaseImageURL(imageName, LOW_RES_PARAMS);
  }, [loadedSlides]);

  return (
    <div className="w-full h-full">
      <Swiper
        id="nostalgia-swiper"
        className="lg:mx-32 rounded-xl"
        modules={[Pagination, Autoplay]}
        slidesPerView={1}
        autoplay={{ delay: 10000, disableOnInteraction: false }}
        pagination={{ clickable: true }}
        onSlideChange={handleSlideChange}
        watchSlidesProgress={true}
      >
        {items?.map(
          (
            {
              _id,
              actionLabel,
              actionLink,
              title,
              backgroundImage,
              overlayImage,
              staticRenderImage,
            },
            index
          ) => <SwiperSlide key={`banner-${index}-${_id || ''}`}>
              {/* Desktop view (LG+) */}
              <div className="hidden min-h-[500px] w-[90%] max-w-[1680px] mx-auto lg:flex items-end">
                <div className="w-full flex justify-around items-end relative overflow-visible rounded-3xl">
                  <div className="absolute inset-0 rounded-3xl overflow-hidden">
                    <img
                      src={getImageSrc(backgroundImage, HI_RES_BG, index)}
                      alt={title}
                      loading={index === 0 ? "eager" : "lazy"}
                      style={{
                        objectFit: 'cover',
                        objectPosition: 'bottom',
                        transition: 'opacity 0.5s ease-in-out',
                        width: '100%'
                      }}
                    />
                  </div>
                  <div className="absolute bottom-[-5px] right-[5%] z-10">
                    <div className="relative" style={{
                      filter: `drop-shadow(#00000085 1rem 1rem 10px)`,
                    }}>
                      <img
                        src={getImageSrc(overlayImage, HI_RES_OVERLAY, index)}
                        alt={title}
                        width={400}
                        height={500}
                        loading={index === 0 ? "eager" : "lazy"}
                        style={{
                          objectFit: 'contain',
                          width: 'auto',
                          height: '500px',
                          transition: 'opacity 0.5s ease-in-out',
                        }}
                      />
                    </div>
                  </div>
                  <div className="min-h-[270px] flex-auto p-10 flex flex-col justify-end lg:items-end gap-5">
                    <h4 className="text-primary-white font-black text-5xl mb-10 drop-shadow-xl">
                      {title}
                    </h4>
                    <Link href={parseShowcaseTargetURI(actionLink)}>
                      <CustomButton
                        label={"تماشا کن"}
                        color="primary"
                        textColor="text-primary-white md:text-lg md:w-[180px] md:h-[50px]"
                        rightIcon={<WatchPlayIcon />}
                      />
                    </Link>
                  </div>
                </div>
              </div>
              {/* Mobile view (<LG) */}
              <Link href={parseShowcaseTargetURI(actionLink)}>
                <div className="w-[90%] mx-auto lg:hidden relative">
                  <img
                    src={getImageSrc(staticRenderImage, HI_RES_STATIC, index)}
                    alt={title}
                    width={800}
                    height={450}
                    loading={index === 0 ? "eager" : "lazy"}
                    style={{
                      objectFit: 'contain',
                      width: '100%',
                      height: 'auto',
                      transition: 'opacity 0.5s ease-in-out',
                    }}
                  />
                </div>
              </Link>
            </SwiperSlide>
        )}
      </Swiper>
    </div>
  );
};

BannerSlider.displayName = 'BannerSlider';

export default BannerSlider;
